import {
  ApplyPromocodeResponseDto,
  ClinicOwnerCreationDto,
  ClinicOwnerResponseDto,
  InitializeSubscriptionResponseDto,
  SubscriptionPackageResponse,
} from '@docbay/schemas';
import { SetupIntent } from 'common/types/stripe';

enum SessionStorage {
  step = 'step',
  clinicOwnerRegistration = 'clinicOwnerRegistration',
  emailInput = 'emailInput',
  isShowBackModal = 'isShowBackModal',
  isReturnToStep4 = 'isReturnToStep4',
  isSentPackage = 'isSentPackage',
  isInvoicing = 'isInvoicing',
  isYearlyPeriod = 'isYearlyPeriod',
}

/**
 * Interface for subscription packages.
 */
interface SubscriptionPackages {
  [quantity: string]: SubscriptionPackageResponse[];
}

/**
 * Interface for clinic owner registration storage.
 */
interface ClinicOwnerRegistrationStorage {
  setupIntent: SetupIntent;
  stripeDebit: InitializeSubscriptionResponseDto;
  subscriptionPackages: SubscriptionPackages;
  subscriptionInvoicingPackages?: SubscriptionPackageResponse;
  contractualAgreement: {
    isAgree: boolean;
  };
  personalInfo: Partial<ClinicOwnerCreationDto> | any;
  timerEndDate: string;
  billingInfo: {
    companyName: string;
    address: {
      city: string;
      country: string;
      postal_code: string;
      address?: string;
    };
    iban: string;
  };
  setPassword: {
    code: number;
    password: string;
  };
  ownerId: Partial<ClinicOwnerResponseDto>;
  promoCode: ApplyPromocodeResponseDto;
  privateDoctor: boolean;
  isYearlyPeriod: boolean;
  isInvoicing: boolean;
}

/**
 * Function to get the current step from sessionStorage.
 */
export const currentStep = parseInt(
  sessionStorage.getItem(SessionStorage.step) || '0',
);

/**
 * Function to set the current step in sessionStorage.
 * @param step - The new step value.
 */
export const setStepSessionStorage = (step: number) => {
  sessionStorage.setItem('step', String(step));
};

/**
 * Function to get the current clinic owner registration from sessionStorage.
 * @returns The current clinic owner registration data.
 */
export const currentClinicOwnerRegistration =
  (): ClinicOwnerRegistrationStorage => {
    const packages = JSON.parse(
      sessionStorage.getItem(SessionStorage.clinicOwnerRegistration) || '{}',
    );

    return {
      setupIntent: packages?.setupIntent,
      stripeDebit: packages?.stripeDebit,
      subscriptionPackages: packages?.subscriptionPackages,
      subscriptionInvoicingPackages: packages.subscriptionInvoicingPackages,
      contractualAgreement: {
        isAgree: packages?.contractualAgreement?.isAgree,
      },
      personalInfo: {
        firstName: packages?.personalInfo?.firstName,
        lastName: packages?.personalInfo?.lastName,
        jobPosition: packages?.personalInfo?.jobPosition,
        email: packages?.personalInfo?.email,
        phone: packages?.personalInfo?.phone,
        supportedCountry: {
          id: packages?.personalInfo?.supportedCountry?.id || '',
          name: packages?.personalInfo?.supportedCountry?.name || '',
          code: packages?.personalInfo?.supportedCountry?.code || '',
        },
      },
      timerEndDate: packages?.timerEndDate?.timerEndDate,
      billingInfo: {
        companyName: packages?.billingInfo?.companyName,
        address: packages?.billingInfo?.address,
        iban: packages?.billingInfo?.iban,
      },
      setPassword: {
        code: packages?.setPassword?.code,
        password: packages?.setPassword?.password,
      },
      ownerId: packages?.ownerId,
      promoCode: packages.promoCode,
      privateDoctor: packages?.privateDoctor,
      isYearlyPeriod: packages.isYearlyPeriod,
      isInvoicing: packages.isInvoicing,
    };
  };

/**
 * Function to set a specific field in the clinic owner registration data in sessionStorage.
 * @param name - The key of the field to update.
 * @param value - The new value for the field.
 */
export const setClinicOwnerRegistrationSessionStorage = (
  name: keyof ClinicOwnerRegistrationStorage,
  value: ClinicOwnerRegistrationStorage[keyof ClinicOwnerRegistrationStorage],
) => {
  sessionStorage.setItem(
    SessionStorage.clinicOwnerRegistration,
    JSON.stringify({
      ...currentClinicOwnerRegistration(),
      [name]: value,
    }),
  );
};

/**
 * Function to check if the email input field should be focused.
 * @returns A boolean indicating whether the email input field should be focused.
 */
export const focusOnEmailInput = () => {
  const isFocused = sessionStorage.getItem(SessionStorage.emailInput)
    ? JSON.parse(sessionStorage.getItem(SessionStorage.emailInput) || '')
    : false;
  return isFocused;
};

/**
 * Function to set whether the email input field should be focused in sessionStorage.
 * @param value - A boolean indicating whether the email input field should be focused.
 */
export const setFocusOnEmailSessionStorage = (value: boolean) => {
  sessionStorage.setItem(SessionStorage.emailInput, JSON.stringify(value));
};

/**
 * Function to set whether the back modal should be shown in sessionStorage.
 */
export const setIsBackModalShowSessionStorage = () => {
  sessionStorage.setItem(SessionStorage.isShowBackModal, 'Y');
};

/**
 * Function to get whether the back modal should be shown from sessionStorage.
 * @returns A boolean indicating whether the back modal should be shown.
 */
export const getIsBackModalShowSessionStorage = () => {
  return sessionStorage.getItem(SessionStorage.isShowBackModal) || 'N';
};

/**
 * Function to clear all clinic owner registration related data from sessionStorage.
 */
export const clearClinicOwnerSessionStorage = () => {
  sessionStorage.removeItem(SessionStorage.step);
  sessionStorage.removeItem(SessionStorage.clinicOwnerRegistration);
  sessionStorage.removeItem(SessionStorage.emailInput);
  sessionStorage.removeItem(SessionStorage.isShowBackModal);
  sessionStorage.removeItem(SessionStorage.isReturnToStep4);
  sessionStorage.removeItem(SessionStorage.isSentPackage);
  sessionStorage.removeItem(SessionStorage.isYearlyPeriod);
  sessionStorage.removeItem(SessionStorage.isInvoicing);
};

/**
 * Function to get the registration token from the clinic owner registration data in sessionStorage.
 * @returns The registration token, or an empty string if it doesn't exist.
 */
export const getRegistrationToken = () => {
  const data = JSON.parse(
    sessionStorage.getItem(SessionStorage.clinicOwnerRegistration)!,
  );

  return data?.stripeDebit?.token || '';
};

/**
 * Function to check whether the package has been sent from sessionStorage.
 * @returns A boolean indicating whether the package has been sent.
 */
export const isSentPackage = () => {
  return sessionStorage.getItem(SessionStorage.isSentPackage) || false;
};

/**
 * Function to set that the package has been sent in sessionStorage.
 */
export const setIsSentPackage = () => {
  return sessionStorage.setItem(SessionStorage.isSentPackage, 'true');
};
